import jsonata from 'jsonata';
import { JSONObject } from '../../types/types';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('resolveExpressions');

// Custom JSONata bindings injected per resolve. Mapping name → impl.
// Names are registered as `$name(...)` in the JSONata expression.
// Async impls are supported – jsonata awaits returned promises.
export type CustomJsonataFunctions = Record<
  string,
  (...args: unknown[]) => unknown
>;

/**
 * Evaluates a JSONata expression against the provided data.
 * Returns the value in its original JSON type (string/number/boolean/object/array/null/undefined).
 *
 * Optional `customFunctions` are registered on the compiled expression as
 * `$name(...)` bindings before evaluation – used e.g. by reference filters
 * to expose `$apiLookup(funcId)` without touching the global JSONata env.
 */
export const resolveStringExpression = async (
  expression: string,
  jsonData: JSONObject,
  customFunctions?: CustomJsonataFunctions
) => {
  if (!expression || !expression.trim()) {
    return undefined;
  }

  try {
    const compiled = jsonata(expression);
    if (customFunctions) {
      Object.entries(customFunctions).forEach(([name, fn]) => {
        compiled.registerFunction(name, fn);
      });
    }
    const result = await compiled.evaluate(jsonData);
    /** @debugFeature resolveExpressions Logs the evaluated JSONata expression, its input data, the names of registered custom functions and the result. */
    log('evaluated expression', () => ({
      expression,
      jsonData,
      customFunctionNames: customFunctions && Object.keys(customFunctions),
      result,
    }));
    return result;
  } catch (error) {
    /** @debugFeature resolveExpressions Logs a JSONata expression that failed to compile or evaluate, with its input data, the names of registered custom functions and the error. */
    log('expression failed', () => ({
      expression,
      jsonData,
      customFunctionNames: customFunctions && Object.keys(customFunctions),
      error,
    }));
    throw new Error(`Error evaluating JSONata expression: ${expression}`, {
      cause: error,
    });
  }
};
